/**
 * UrgenciaDropdown.tsx — Multi-select urgencia con texto coloreado
 *
 * Posición: extremo DERECHO del FilterRow (separado de los pills de vista)
 *
 * UID-03b | Fase 3 | feat/step3-coteja
 * @date 2026-03-06
 */

import { useState, useEffect, useRef, type FC, type CSSProperties } from 'react';
import { colors, typography, borderRadius, zIndex } from '@/design-system/v2';
import { URGENCIA_CONFIG, type UrgenciaNivel } from './types';

interface UrgenciaDropdownProps {
  selected: UrgenciaNivel[];
  onChange: (vals: UrgenciaNivel[]) => void;
}

const NIVELES: UrgenciaNivel[] = ['alta', 'media', 'baja', 'ninguna'];

export const UrgenciaDropdown: FC<UrgenciaDropdownProps> = ({ selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (v: UrgenciaNivel) => {
    onChange(selected.includes(v) ? selected.filter(s => s !== v) : [...selected, v]);
  };


  const hasFilter = selected.length > 0 && selected.length < NIVELES.length;

  const triggerStyle: CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    padding: '5px 11px', borderRadius: borderRadius.full, cursor: 'pointer',
    border: `1px solid ${hasFilter ? colors.warning : colors.border.DEFAULT}`,
    background: hasFilter ? colors.warningMuted : 'transparent',
    color: hasFilter ? colors.warning : colors.fg.secondary,
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.sans,
    userSelect: 'none',
    transition: 'all 0.15s',
  };

  const dropStyle: CSSProperties = {
    position: 'absolute', top: 'calc(100% + 6px)', right: 0,
    background: colors.bg.secondary,
    border: `1px solid ${colors.border.DEFAULT}`,
    borderRadius: '8px',
    padding: '6px',
    minWidth: '160px',
    zIndex: zIndex.dropdown,
    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
  };

  const itemStyle = (nivel: UrgenciaNivel): CSSProperties => {
    const active = selected.includes(nivel);
    const cfg = URGENCIA_CONFIG[nivel];
    return {
      display: 'flex', alignItems: 'center', gap: '8px',
      padding: '6px 10px', borderRadius: '6px', cursor: 'pointer',
      background: active ? `${cfg.color}18` : 'transparent',
      color: active ? cfg.color : colors.fg.secondary,
      fontSize: typography.fontSize.sm,
      fontFamily: typography.fontFamily.sans,
      transition: 'background 0.1s',
    };
  };

  const dotStyle = (color: string): CSSProperties => ({
    width: '8px', height: '8px', borderRadius: '50%', background: color, flexShrink: 0,
  });

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div style={triggerStyle} onClick={() => setOpen(o => !o)}
        role="button" aria-haspopup="listbox" aria-expanded={open}
        tabIndex={0} onKeyDown={e => e.key === 'Enter' && setOpen(o => !o)}>
        Urgencia
        {hasFilter && (
          <span style={{
            fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs,
            padding: '1px 5px', borderRadius: '9999px',
            background: `${colors.warning}28`, color: colors.warning,
          }}>{selected.length}</span>
        )}
        {/* Chevron */}
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor"
          strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: '0.15s' }}>
          <polyline points="2 4 6 8 10 4" />
        </svg>
      </div>

      {open && (
        <div style={dropStyle} role="listbox" aria-multiselectable>
          {NIVELES.map(nivel => (
            <div key={nivel} style={itemStyle(nivel)} onClick={() => toggle(nivel)}
              role="option" aria-selected={selected.includes(nivel)}>
              <span style={dotStyle(URGENCIA_CONFIG[nivel].color)} />
              <span style={{ color: URGENCIA_CONFIG[nivel].color }}>
                {URGENCIA_CONFIG[nivel].label}
              </span>
              {selected.includes(nivel) && (
                <svg style={{ marginLeft: 'auto' }} width="12" height="12" viewBox="0 0 12 12"
                  fill="none" stroke={URGENCIA_CONFIG[nivel].color} strokeWidth="2.5"
                  strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="2 6 5 9 10 3" />
                </svg>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
